import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { agentModel, agentModelSet, type AgentRow } from '../transport/api'
import { beginRestart, endRestart } from './restarting'
import { whyRestartFailed } from './restartError'

/**
 * MODEL AND EFFORT, on the agent's card — owner, 2026-09-26: 讓使用者可以看到它的 model
 * 以及 Effort 並且可以直接使用指令來更改它的模型以及 Effort 直接跟前端接起來.
 *
 * What it shows is the agent's OWN record (its transcript, codex's app-server, grok's
 * history, agy's log, cursor's chat store), read by the edge. What it offers is only
 * what that CLI itself lists. A change restarts the agent with the CLI's own launch
 * flags — the conversation survives and the choice is remembered for every later
 * restart — so it raises the same cover the Restart button does.
 *
 * Effort has its own control only for claude, codex and grok. agy and cursor build it
 * into the model name ("Gemini 3.8 Flash (High)", "Codex 5.3 High"), so there the one
 * model list IS the effort choice, and a second control would do nothing.
 */
export function AgentModelSection({ agent, deviceId }: { agent: AgentRow; deviceId: string | null }) {
  const qc = useQueryClient()
  const key = ['agent-model', deviceId, agent.host ?? null, agent.session]
  const info = useQuery({
    queryKey: key,
    queryFn: () => agentModel(agent.session, agent.host ?? null, deviceId),
    // The lists come from each CLI and change rarely; the edge caches them too.
    staleTime: 30_000,
    retry: false,
  })

  const d = info.data
  const current = d?.current
  const models = useMemo(() => d?.choices?.models ?? [], [d])
  const [model, setModel] = useState('')
  const [effort, setEffort] = useState('')

  /*
   * THE SELECTORS START WHERE THE AGENT IS — owner, 2026-09-26: 上面顯示的 Default 的模型
   * 跟下面下拉選單顯示的模型是不一樣的. They used to start at the saved CHOICE while the
   * text showed the reading, so the two could disagree (a switch codex did not take left
   * "medium" chosen on an agent running "high"). Now both show the running process: the
   * text precisely, the selectors as the offered choice it corresponds to. The saved
   * choice is only the fallback when the edge cannot place the running model.
   */
  const baseModel = current?.choice ?? d?.chosen?.model ?? ''
  const baseEffort = current?.effort ?? d?.chosen?.effort ?? ''
  useEffect(() => {
    if (!d?.supported) return
    setModel(models.some((x) => x.id === baseModel) ? baseModel : '')
    setEffort(baseEffort)
  }, [d, models, baseModel, baseEffort])

  const efforts = useMemo(() => {
    if (!d?.choices?.efforts) return null
    const own = models.find((x) => x.id === model)?.efforts
    return own && own.length ? own : d.choices.efforts
  }, [d, models, model])

  const change = useMutation({
    mutationFn: () => {
      beginRestart(agent.session)
      return agentModelSet(agent.session, agent.host ?? null, deviceId, {
        ...(model ? { model } : {}),
        ...(efforts && effort ? { effort } : {}),
      })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agent-model'] })
      void qc.invalidateQueries({ queryKey: ['agents'] })
    },
    onSettled: (_d, err) => endRestart(agent.session, err ? 'refused' : 'ok'),
  })

  if (info.isLoading) {
    return (
      <p className="ac-model-why">Reading…</p>
    )
  }
  // An edge too old to have the route answers 404: say nothing rather than an error.
  if (info.isError || !d?.supported) return null

  // "Changed" means different from what it RUNS ON — the thing a switch would change.
  const dirty = (model !== '' && model !== baseModel) || (efforts !== null && effort !== '' && effort !== baseEffort)
  const labelOf = (id: string | null | undefined) => models.find((x) => x.id === id)?.label ?? id ?? 'default'

  return (
    <>
      <div className="ac-model">
        <p className="ac-model-now" data-testid="model-now">
          <strong>{labelOf(current?.model)}</strong>
          {current?.effort ? <> · {current.effort} effort</> : null}
        </p>
        <label className="sm-field">
          <span className="sm-label">Model</span>
          <select
            className="sm-input"
            aria-label="Model"
            value={model}
            disabled={change.isPending || models.length === 0}
            onChange={(e) => setModel(e.target.value)}
          >
            {model === '' && <option value="">Keep the current model</option>}
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        </label>
        {efforts && (
          <label className="sm-field">
            <span className="sm-label">Effort</span>
            <select
              className="sm-input"
              aria-label="Effort"
              value={efforts.includes(effort) ? effort : ''}
              disabled={change.isPending}
              onChange={(e) => setEffort(e.target.value)}
            >
              {!efforts.includes(effort) && <option value="">Keep the current effort</option>}
              {efforts.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
          </label>
        )}
        <button
          type="button"
          className="ac-act"
          disabled={!dirty || change.isPending}
          onClick={() => change.mutate()}
        >
          {change.isPending ? 'Switching…' : 'Switch and restart'}
        </button>
        {change.isError && <p className="ac-model-bad">{whyRestartFailed(change.error)}</p>}
        {change.isSuccess &&
          (change.data?.notApplied ? (
            // Restarted, but the CLI kept its setting: said plainly, never shown as done.
            <p className="ac-model-bad">{change.data.notApplied.detail}</p>
          ) : (
            <p className="ac-model-ok">Switched — same conversation.</p>
          ))}
      </div>
    </>
  )
}
